<template>
	<div>
		<wait-modal id="wait-modal" ref="waitModal" :title="translation.title"></wait-modal>
		<conclusion-modal id="conclusion-modal" ref="conclusionModal"></conclusion-modal>

		<section class="new-event-flight-reservation__form-intro">
			<button type="button" class="new-event-flight-reservation__toggle" :aria-label="translation.formTitle">
				<span></span>
			</button>
			<h3>{{translation.formTitle}}</h3>
			<p>{{translation.descriptionForm}}</p>
		</section>

		<form class="new-event-flight-reservation__offline-form" @submit.prevent="sendReservation">
			<h4>{{translation.personalDetails}}</h4>

			<div class="new-event-flight-reservation__field new-event-flight-reservation__field--full">
				<b-form-input v-model="reservation.pax.name" class="new-event-flight-reservation__control" type="text" :placeholder="readableLabel(translation.fullName)" required/>
			</div>
			<div class="new-event-flight-reservation__field">
				<b-form-input v-model="reservation.pax.phone" class="new-event-flight-reservation__control" type="tel" :placeholder="readableLabel(translation.phone)" required/>
			</div>
			<div class="new-event-flight-reservation__field">
				<b-form-input v-model="reservation.pax.email" class="new-event-flight-reservation__control" type="email" :placeholder="readableLabel(translation.email)" required/>
			</div>

			<div class="new-event-flight-reservation__field">
				<label>{{readableLabel(translation.departureAirport)}}</label>
				<autocomplete
					ref="departureAutocomplete"
					v-model="reservation.airport"
					class="new-event-flight-reservation__autocomplete"
					:options="debounce(findAirports, 300)"
					track-by="initials"
					label-by="label"
					:placeholder="translation.originPlaceholder"
				/>
			</div>

			<div class="new-event-flight-reservation__field new-event-flight-reservation__field--class">
				<b-form-input v-model="reservation.flightClass" class="new-event-flight-reservation__control new-event-flight-reservation__control--accent" type="text" :placeholder="readableLabel(translation.class)"/>
			</div>

			<div class="new-event-flight-reservation__dates">
				<div class="new-event-flight-reservation__date-group">
					<date-picker v-model="departureDate" class="new-event-flight-reservation__date" :initialDefaultDate="initialDefaultDate" :placeholder="readableLabel(translation.departureDate)" required/>
					<div class="new-event-flight-reservation__periods">
						<label><input type="radio" v-model="reservation.departurePeriod" :value="Period.MOURNING.name"> {{translation.mourning}}</label>
						<label><input type="radio" v-model="reservation.departurePeriod" :value="Period.AFTERNOON.name"> {{translation.afternoon}}</label>
						<label><input type="radio" v-model="reservation.departurePeriod" :value="Period.EVENING.name"> {{translation.evening}}</label>
					</div>
				</div>

				<div class="new-event-flight-reservation__date-group">
					<date-picker v-model="returnDate" class="new-event-flight-reservation__date" :initialDefaultDate="initialDefaultDate" :placeholder="readableLabel(translation.returnDate)" required/>
					<div class="new-event-flight-reservation__periods">
						<label><input type="radio" v-model="reservation.returnPeriod" :value="Period.MOURNING.name"> {{translation.mourning}}</label>
						<label><input type="radio" v-model="reservation.returnPeriod" :value="Period.AFTERNOON.name"> {{translation.afternoon}}</label>
						<label><input type="radio" v-model="reservation.returnPeriod" :value="Period.EVENING.name"> {{translation.evening}}</label>
					</div>
				</div>
			</div>

			<div class="new-event-flight-reservation__actions">
				<button type="button" class="new-event-flight-reservation__button new-event-flight-reservation__button--secondary" @click="$emit('close')">voltar</button>
				<button type="submit" class="new-event-flight-reservation__button new-event-flight-reservation__button--primary">{{translation.send}}</button>
			</div>
		</form>
	</div>
</template>

<script>
import debounce from 'debounce-promise';
import moment from 'moment';
import { translate } from '../../assets/translations';
import AirportService from '../../services/AirportService';
import EventService from '../../services/EventService';
import FlightRequest from '../../model/FlightRequestOffline';
import Period from '../../enums/Period';
import Autocomplete from '../shared/Autocomplete.vue';
import ConclusionModal from '../shared/ConclusionModal.vue';
import DatePicker from '../shared/DatePicker.vue';
import WaitModal from '../shared/WaitModal.vue';

export default {
	components: {
		Autocomplete,
		ConclusionModal,
		DatePicker,
		WaitModal,
	},
	props: {
		event: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			Period,
			airportService: new AirportService(),
			eventService: new EventService(),
			departureDate: null,
			returnDate: null,
			reservation: new FlightRequest(),
		};
	},
	computed: {
		initialDefaultDate() {
			return this.event && this.event.startDate
				? moment(this.event.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
				: moment().format('DD/MM/YYYY');
		},
		translation() {
			return translate(this.$store.state.language).flightRequestOffline;
		},
	},
	methods: {
		debounce,
		cleanForm() {
			if (this.$refs.departureAutocomplete) this.$refs.departureAutocomplete.clear();
			this.reservation = new FlightRequest();
			this.departureDate = null;
			this.returnDate = null;
		},
		findAirports(query) {
			return this.airportService.findAirports(query).then(response => response.data);
		},
		formatDate(date) {
			return moment(date).format('DD/MM/YYYY');
		},
		readableLabel(label) {
			return String(label || '').toLowerCase().replace(/(^|\s|-)\S/g, letter => letter.toUpperCase());
		},
		sendReservation() {
			if (!this.departureDate || !this.returnDate || !(this.departureDate < this.returnDate)) {
				alert(this.translation.invalidDate);
				return;
			}

			const conclusion = this.translation.conclusion;
			let success = true;
			let title = conclusion.success.title;
			let message = conclusion.success.message;
			this.reservation.departureDate = this.formatDate(this.departureDate);
			this.reservation.returnDate = this.formatDate(this.returnDate);
			this.$refs.waitModal.open(this.translation.wait);
			this.eventService.sendFlightReservation(this.event.id, this.reservation)
				.then(() => this.cleanForm())
				.catch(() => {
					success = false;
					title = conclusion.fail.title;
					message = conclusion.fail.message;
				})
				.finally(() => {
					this.$refs.conclusionModal.open(success, title, message);
					this.$refs.waitModal.close();
				});
		},
	},
};
</script>



// WEBPACK FOOTER //
// src/components/flight-reservation/FlightRequestOffline.vue